/** `sections/header.liquid` — `<site-header>`: cart badge, exclusive `<details>`, US state region popover (Figma Select State). */

const REGION_LS_KEY = 'wyld_us_state'
const REGION_DATA_COMMITTED = 'regionStateCommitted'
const GEO_URL = 'https://wyld-app.vercel.app/geo'

const PRODUCT_AVAILABILITY_SELECTOR = '[data-product-availability]'
const REGION_STATUS_SELECTOR = '[data-region-status]'

const US_STATES = new Set([
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
])

function parseJsonAttr(el, attr) {
  const raw = el?.getAttribute(attr)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// Region detection lives in the header because it renders on every page.
// `detectStateFromIP` only resolves the visitor's US state (or null); the
// caller decides whether to persist/broadcast it, so it can avoid overriding
// an existing selection (except California, which is forced — see
// `#initRegionDetection`).
let geoPromise = null

function detectStateFromIP() {
  if (geoPromise) return geoPromise
  geoPromise = fetch(GEO_URL)
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (data?.country?.code !== 'US') return null
      const region = data.state?.name
      if (!region || !US_STATES.has(region)) return null
      return region
    })
    .catch(() => null)
  return geoPromise
}

function isAvailableInState(includedStates, state) {
  if (includedStates.length === 0) return true
  return includedStates.includes(state)
}

const MENU_BODY_CLASS = {
  shop: 'shop-menu-open',
  learn: 'learn-menu-open',
}

const SEARCH_BODY_CLASS = 'search-open'

const HEADER_SCROLL_THRESHOLD = 150

class SiteHeader extends HTMLElement {
  #abort

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#syncCartBadge()
    document.addEventListener('theme:cart:updated', () => this.#syncCartBadge(), { signal })

    this.querySelectorAll('details.disclosure[name]').forEach((details) => {
      details.addEventListener(
        'toggle',
        () => {
          if (details.open) {
            this.#closeSiblingDetails(details)
            this.#closeRegionPopoverIfOpen()
          }
        },
        { signal },
      )
    })

    this.#initRegionPopover(signal)
    this.#initRegionStatus(signal)
    this.#initMenuToggles(signal)
    this.#initSearchToggle(signal)
    this.#initOverlay(signal)
    this.#initMobileMenuToggle(signal)
    this.#initHeaderOffset(signal)
    this.#initScrollState(signal)

    this.#initRegionDetection()
  }

  disconnectedCallback() {
    this.#abort.abort()
    this.#closeAllMenus()
    this.#closeSearch()
  }

  #initMenuToggles(signal) {
    const buttons = this.querySelectorAll('[data-menu-toggle]')
    if (!buttons.length) return

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const key = btn.dataset.menuToggle
          if (!key || !MENU_BODY_CLASS[key]) return
          const isOpen = btn.getAttribute('aria-expanded') === 'true'
          if (isOpen) {
            this.#closeMenu(key)
          } else {
            this.#openMenu(key)
          }
        },
        { signal },
      )
    })

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return
        this.#closeAllMenus()
      },
      { signal },
    )

    document.addEventListener(
      'click',
      (e) => {
        if (!this.#isAnyMenuOpen()) return
        if (this.contains(e.target)) return
        this.#closeAllMenus()
      },
      { signal },
    )
  }

  #isAnyMenuOpen() {
    return Object.values(MENU_BODY_CLASS).some((cls) => document.body.classList.contains(cls))
  }

  #initSearchToggle(signal) {
    // Both header bars (normal + scrolled) carry a search toggle, so bind them
    // all — otherwise only the first-rendered bar's button works.
    const buttons = this.querySelectorAll('[data-search-toggle]')
    if (!buttons.length) return

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          if (this.#isSearchOpen()) {
            this.#closeSearch()
          } else {
            this.#openSearch()
          }
        },
        { signal },
      )
    })

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return
        if (!this.#isSearchOpen()) return
        this.#closeSearch()
        const visibleBtn = Array.from(buttons).find((b) => b.offsetParent !== null)
        ;(visibleBtn || buttons[0]).focus()
      },
      { signal },
    )

    document.addEventListener(
      'click',
      (e) => {
        if (!this.#isSearchOpen()) return
        if (this.contains(e.target)) return
        this.#closeSearch()
      },
      { signal },
    )
  }

  #isSearchOpen() {
    return document.body.classList.contains(SEARCH_BODY_CLASS)
  }

  // The dimming overlay is a child of the header, so the document-level
  // click-outside handlers (which bail on clicks inside the header) never fire
  // for it — close the open menu/search explicitly on overlay click.
  #initOverlay(signal) {
    const overlay = this.querySelector('[data-header-overlay]')
    if (!overlay) return
    overlay.addEventListener(
      'click',
      () => {
        this.#closeAllMenus()
        this.#closeSearch()
      },
      { signal },
    )
  }

  #openSearch() {
    this.#closeAllMenus()
    this.#closeRegionPopoverIfOpen()
    document.body.classList.add(SEARCH_BODY_CLASS)
    this.querySelectorAll('[data-search-toggle]').forEach((b) => {
      b.setAttribute('aria-expanded', 'true')
      b.setAttribute('aria-label', 'Close search')
    })
    requestAnimationFrame(() => {
      this.querySelector('[data-search-input]')?.focus()
    })
  }

  #closeSearch() {
    document.body.classList.remove(SEARCH_BODY_CLASS)
    this.querySelectorAll('[data-search-toggle]').forEach((b) => {
      b.setAttribute('aria-expanded', 'false')
      b.setAttribute('aria-label', 'Search')
    })
    // Collapse the region popover nested in the search panel without stealing focus
    // (the panel is about to become inert).
    this.querySelectorAll('search-panel [data-header-region]').forEach((wrap) => {
      const btn = wrap.querySelector('[data-region-trigger]')
      if (btn?.getAttribute('aria-expanded') !== 'true') return
      this.#revertRegionSelect(wrap)
      this.#closeRegionPopover(wrap, { focusTrigger: false })
    })
  }

  // Toggle the collapsed header state once the page scrolls past the threshold.
  // The `.header-scrolled` class drives the CSS size animation (min-height,
  // padding, logo, notification bar).
  #initScrollState(signal) {
    const update = () => {
      this.classList.toggle('header-scrolled', window.scrollY > HEADER_SCROLL_THRESHOLD)
    }
    update()
    window.addEventListener('scroll', update, { passive: true, signal })
  }

  // The full (unscrolled) header height, frozen while the header is collapsed on
  // scroll so `.site-main` content never jumps when it shrinks. Safe to call from
  // the ResizeObserver: it only reads layout, it never mutates the class.
  #setFullOffset() {
    if (this.classList.contains('header-scrolled')) return
    const height = this.offsetHeight
    if (height) {
      document.documentElement.style.setProperty('--header-offset', `${height}px`)
    }
  }

  // The collapsed header height, which sticky elements (cart summary, product
  // slideshow, mobile menu) clear once the header has shrunk on scroll. This is
  // deliberately kept OFF the ResizeObserver path: probing it requires briefly
  // applying `.header-scrolled`, and doing that on every observer callback (which
  // fires each frame during the header's own transition) created a feedback loop
  // that reset the class every frame and flashed the notification bar.
  #setScrolledOffset() {
    let height
    if (this.classList.contains('header-scrolled')) {
      height = this.offsetHeight
    } else {
      // Apply the class synchronously to read the collapsed height, then revert
      // before the next paint so it never becomes visible.
      this.classList.add('header-scrolled')
      height = this.offsetHeight
      this.classList.remove('header-scrolled')
    }
    if (height) {
      document.documentElement.style.setProperty('--header-offset-scrolled', `${height}px`)
    }
  }

  #initHeaderOffset(signal) {
    const measure = () => {
      this.#setFullOffset()
      this.#setScrolledOffset()
    }
    measure()
    requestAnimationFrame(measure)

    // A ResizeObserver re-measures the full offset whenever the header's real
    // height changes — late web-font loads, the logo image loading, the
    // notification bar appearing, and viewport changes. It fires every frame
    // during the header's size transition, so it must only read (never toggle
    // the class).
    const supportsResizeObserver = typeof ResizeObserver !== 'undefined'
    if (supportsResizeObserver) {
      const observer = new ResizeObserver(() => this.#setFullOffset())
      observer.observe(this)
      signal.addEventListener('abort', () => observer.disconnect())
    } else {
      window.addEventListener('resize', () => this.#setFullOffset(), { passive: true, signal })
    }

    // The collapsed height only changes across breakpoints, so re-probe it on
    // viewport resize (outside the ResizeObserver) rather than every frame.
    window.addEventListener('resize', () => this.#setScrolledOffset(), { passive: true, signal })

    // Fonts can finish loading after the first measurement on some browsers;
    // re-measure once they're ready as a belt-and-suspenders pass.
    document.fonts?.ready?.then(measure)
  }

  // Both header bars carry a mobile toggle, so bind them all and keep their
  // state in sync.
  #initMobileMenuToggle(signal) {
    const buttons = this.querySelectorAll('[data-mobile-menu-toggle]')
    if (!buttons.length) return

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const isOpen = btn.getAttribute('aria-expanded') === 'true'
          this.#setMobileMenu(!isOpen)
        },
        { signal },
      )
    })

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return
        if (!document.body.classList.contains('mobile-menu-open')) return
        this.#setMobileMenu(false)
      },
      { signal },
    )
  }

  #setMobileMenu(open) {
    this.querySelectorAll('[data-mobile-menu-toggle]').forEach((btn) => {
      btn.setAttribute('aria-expanded', String(open))
      btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu')
    })
    document.body.classList.toggle('mobile-menu-open', open)
  }

  #openMenu(key) {
    this.#closeSearch()
    Object.keys(MENU_BODY_CLASS).forEach((k) => {
      if (k !== key) this.#closeMenu(k)
    })
    document.body.classList.add(MENU_BODY_CLASS[key])
    this.querySelectorAll(`[data-menu-toggle="${key}"]`).forEach((b) => {
      b.setAttribute('aria-expanded', 'true')
    })
  }

  #closeMenu(key) {
    document.body.classList.remove(MENU_BODY_CLASS[key])
    this.querySelectorAll(`[data-menu-toggle="${key}"]`).forEach((b) => {
      b.setAttribute('aria-expanded', 'false')
    })
  }

  #closeAllMenus() {
    Object.keys(MENU_BODY_CLASS).forEach((k) => this.#closeMenu(k))
  }

  #syncCartBadge() {
    this.querySelectorAll('.cart-link [data-cart-count]').forEach((el) => {
      const link = el.closest('.cart-link')
      if (!link) return
      const n = Number.parseInt(String(el.textContent).trim(), 10)
      link.classList.toggle('cart-link-has-items', Number.isFinite(n) && n > 0)
    })
  }

  #closeSiblingDetails(opened) {
    const name = opened.getAttribute('name')
    if (!name) return
    this.querySelectorAll(`details.disclosure[name="${CSS.escape(name)}"]`).forEach((d) => {
      if (d !== opened) d.removeAttribute('open')
    })
  }

  #safeLocalStorageGet(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }

  #revertRegionSelect(wrap) {
    const select = wrap.querySelector('[data-region-state-select]')
    const committed = wrap.dataset[REGION_DATA_COMMITTED]
    if (!select || !committed) return
    const match = Array.from(select.options).find((o) => o.value === committed)
    if (match) select.value = committed
  }

  #initRegionPopover(signal) {
    // The header has one region control and the mobile search panel has another;
    // initialise each and keep them in sync via `theme:region:state-changed`.
    this.querySelectorAll('[data-header-region]').forEach((wrap) => this.#initRegionWrap(wrap, signal))
  }

  // The region status line ("Ordering disabled") is only relevant on a product
  // page when that product is not available in the visitor's selected state.
  // It stays hidden everywhere else (no product context, or product available).
  #initRegionStatus(signal) {
    const statusEls = this.querySelectorAll(REGION_STATUS_SELECTOR)
    if (!statusEls.length) return

    const apply = (state) => {
      const availabilityEl = document.querySelector(PRODUCT_AVAILABILITY_SELECTOR)
      let show = false
      if (availabilityEl && state) {
        const included = parseJsonAttr(availabilityEl, 'data-included-states')
        show = !isAvailableInState(included, state)
      }
      statusEls.forEach((el) => {
        el.hidden = !show
      })
    }

    apply(this.#safeLocalStorageGet(REGION_LS_KEY))

    document.addEventListener(
      'theme:region:state-changed',
      (e) => apply(e.detail?.state || ''),
      { signal },
    )
  }

  // IP detection on every page load. California is forced: the visitor's region
  // is set to California regardless of any prior selection, and the region
  // control is locked so it can't be changed. Any other state only populates
  // the region when nothing is stored — it never overrides a saved preference.
  #initRegionDetection() {
    detectStateFromIP().then((region) => {
      if (!region) return
      if (region === 'California') {
        this.#commitRegionState('California')
        this.#lockRegion()
        return
      }
      if (!this.#safeLocalStorageGet(REGION_LS_KEY)) {
        this.#commitRegionState(region)
      }
    })
  }

  #commitRegionState(state) {
    try {
      localStorage.setItem(REGION_LS_KEY, state)
    } catch {
      /* private mode */
    }
    document.dispatchEvent(
      new CustomEvent('theme:region:state-changed', {
        bubbles: true,
        detail: { state },
      }),
    )
  }

  // Disable every region control (header + search panel) so the locked-in state
  // can't be changed, closing any open popover first.
  #lockRegion() {
    this.querySelectorAll('[data-header-region]').forEach((wrap) => {
      this.#closeRegionPopover(wrap, { focusTrigger: false })
      wrap.classList.add('region-locked')
      const trigger = wrap.querySelector('[data-region-trigger]')
      if (trigger) {
        trigger.disabled = true
        trigger.setAttribute('aria-disabled', 'true')
      }
      const select = wrap.querySelector('[data-region-state-select]')
      if (select) select.disabled = true
      const saveBtn = wrap.querySelector('[data-region-save]')
      if (saveBtn) saveBtn.disabled = true
    })
  }

  #initRegionWrap(wrap, signal) {
    const btn = wrap.querySelector('[data-region-trigger]')
    const panel = wrap.querySelector('[data-region-panel]')
    const select = wrap.querySelector('[data-region-state-select]')
    const saveBtn = wrap.querySelector('[data-region-save]')
    if (!btn || !panel || !select) return

    const stored = this.#safeLocalStorageGet(REGION_LS_KEY)
    if (stored) {
      const match = Array.from(select.options).find((o) => o.value === stored)
      if (match) {
        select.value = stored
        this.#syncRegionButtonText(wrap, stored)
      }
    }

    wrap.dataset[REGION_DATA_COMMITTED] = select.value

    const commitFromSelect = () => {
      const v = select.value
      wrap.dataset[REGION_DATA_COMMITTED] = v
      try {
        localStorage.setItem(REGION_LS_KEY, v)
      } catch {
        /* private mode */
      }
      this.#syncRegionButtonText(wrap, v)
      this.dispatchEvent(
        new CustomEvent('theme:region:state-changed', {
          bubbles: true,
          composed: true,
          detail: { state: v, source: wrap },
        }),
      )
    }

    btn.addEventListener(
      'click',
      () => {
        const open = btn.getAttribute('aria-expanded') === 'true'
        if (open) {
          this.#revertRegionSelect(wrap)
          this.#closeRegionPopover(wrap)
        } else {
          this.#openRegionPopover(wrap)
        }
      },
      { signal },
    )

    saveBtn?.addEventListener(
      'click',
      () => {
        commitFromSelect()
        this.#closeRegionPopover(wrap)
      },
      { signal },
    )

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return
        if (btn.getAttribute('aria-expanded') !== 'true') return
        this.#revertRegionSelect(wrap)
        this.#closeRegionPopover(wrap)
      },
      { signal },
    )

    document.addEventListener(
      'click',
      (e) => {
        if (btn.getAttribute('aria-expanded') !== 'true') return
        if (wrap.contains(e.target)) return
        this.#revertRegionSelect(wrap)
        this.#closeRegionPopover(wrap)
      },
      { signal },
    )

    // Mirror a commit made on the other region control onto this one.
    document.addEventListener(
      'theme:region:state-changed',
      (e) => {
        const v = e.detail?.state
        if (!v || e.detail?.source === wrap) return
        wrap.dataset[REGION_DATA_COMMITTED] = v
        const match = Array.from(select.options).find((o) => o.value === v)
        if (match) select.value = v
        this.#syncRegionButtonText(wrap, v)
      },
      { signal },
    )
  }

  #closeRegionPopoverIfOpen() {
    this.querySelectorAll('[data-header-region]').forEach((wrap) => {
      const btn = wrap.querySelector('[data-region-trigger]')
      if (btn?.getAttribute('aria-expanded') !== 'true') return
      this.#revertRegionSelect(wrap)
      this.#closeRegionPopover(wrap)
    })
  }

  #openRegionPopover(wrap) {
    const btn = wrap.querySelector('[data-region-trigger]')
    const panel = wrap.querySelector('[data-region-panel]')
    if (!btn || !panel) return
    // The search-panel region lives inside the search panel, so opening it must
    // not close the panel; only the header region is mutually exclusive with search.
    if (!wrap.closest('search-panel')) {
      this.#closeSearch()
    }
    this.querySelectorAll('details.disclosure[open]').forEach((d) => d.removeAttribute('open'))
    panel.classList.remove('hidden')
    btn.setAttribute('aria-expanded', 'true')
    requestAnimationFrame(() => {
      wrap.querySelector('[data-region-state-select]')?.focus()
    })
  }

  #closeRegionPopover(wrap, { focusTrigger = true } = {}) {
    const btn = wrap.querySelector('[data-region-trigger]')
    const panel = wrap.querySelector('[data-region-panel]')
    if (!btn || !panel) return
    panel.classList.add('hidden')
    btn.setAttribute('aria-expanded', 'false')
    if (focusTrigger) btn.focus()
  }

  #syncRegionButtonText(wrap, stateName) {
    const el = wrap.querySelector('[data-region-button-text]')
    if (el) el.textContent = stateName
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader)
}
